/* eslint-disable */
import React, { useMemo } from "react";
import {
  Modal,
  Stack,
  Text,
  Group,
  Button,
  Badge,
  Center,
} from "@mantine/core";
import {
  IconPlayerPlay,
  IconEdit,
  IconX,
  IconExternalLink,
} from "@tabler/icons-react";
import { Meeting } from "features/meeting";
import { useGetMeeting } from "api/meeting/getMeetingById";

type Props = {
  opened: boolean;
  currentMeetingId: string | any;
  onClose: () => void;
  onJoin: (meeting: Meeting) => void;
  onEdit: (meetingId: string) => void;
  onCancel: (meetingId: string) => void;
};

export default function MeetingDetailsModal({
  opened,
  onClose,
  currentMeetingId,
  onJoin,
  onEdit,
  onCancel,
}: Props) {
  const { data, isLoading, isError } = useGetMeeting(currentMeetingId);
  console.log("MeetingDetailsModal data:", currentMeetingId);

  const raw = data?.data;

  const meeting = useMemo<Meeting | null>(() => {
    if (!raw) return null;

    const attendees = (raw.attendees || []).map((a: any) => ({
      user: a?.user?._id ?? (typeof a.user === "string" ? a.user : ""),
      userType: a?.userType ?? "User",
    }));

    const mapped: any = {
      _id: raw._id,
      id: raw.id ?? raw._id,
      title: raw.title,
      description: raw.description,
      attendees,
      scheduledDate: raw.scheduledDate,
      duration: raw.duration,
      meetingPlatform: raw.meetingPlatform,
      projectId: raw.projectId,
      agenda: raw.agenda,
      meetingLink: (raw as any).meetingLink ?? undefined,
      isRecurring: raw.isRecurring,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
    };

    return mapped as Meeting;
  }, [raw]);

  const displayAttendees = useMemo(() => {
    if (!raw?.attendees) return [];
    return raw.attendees.map((a: any) => {
      const user = a.user || {};
      const name =
        [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
        user.email ||
        user._id;
      return {
        key: a._id,
        name,
        userType: a.userType,
        status: a.status ?? "pending",
      };
    });
  }, [raw]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={meeting?.title || "Meeting Details"}
      size="lg"
      overlayOpacity={0.5}
      shadow="xs"
      // withCloseButton={false}
      transitionDuration={300}
      centered
    >
      {isLoading ? (
        <Center style={{ minHeight: 160 }}>
          <Text>Loading meeting details...</Text>
        </Center>
      ) : isError ? (
        <Center style={{ minHeight: 160 }}>
          <Text color="red">Failed to load meeting.</Text>
        </Center>
      ) : meeting ? (
        <Stack>
          <Text>
            <b>Scheduled:</b>{" "}
            {meeting.scheduledDate
              ? new Date(meeting.scheduledDate).toLocaleString()
              : "N/A"}
          </Text>

          <Text>
            <b>Duration:</b> {meeting.duration ?? "N/A"} minutes
          </Text>

          <Text>
            <b>Platform:</b> {meeting.meetingPlatform || "N/A"}
          </Text>

          {meeting.meetingLink && (
            <Text>
              <IconExternalLink
                style={{ verticalAlign: "middle", marginRight: 6 }}
              />
              <a href={meeting.meetingLink} target="_blank" rel="noreferrer">
                Meeting Link
              </a>
            </Text>
          )}

          <div>
            <Text weight={700}>Description</Text>
            <Text color="dimmed">{meeting.description ?? "—"}</Text>
          </div>

          <div>
            <Text weight={700}>Agenda</Text>
            <pre style={{ whiteSpace: "pre-wrap" }}>
              {meeting.agenda ?? "—"}
            </pre>
          </div>

          <div>
            <Text weight={700}>Attendees</Text>
            <Group mt="xs">
              {displayAttendees.length ? (
                displayAttendees.map((a) => (
                  <Badge key={a.key}>
                    {a.name} ({a.userType}) - {a.status}
                  </Badge>
                ))
              ) : (
                <Text color="dimmed">No attendees</Text>
              )}
            </Group>
          </div>

          <Group position="right" mt="md">
            <Button
              onClick={() => onJoin(meeting)}
              leftIcon={<IconPlayerPlay />}
            >
              Join Meeting
            </Button>

            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Center>No meeting selected</Center>
      )}
    </Modal>
  );
}
